import { createSelector } from "reselect";
import take from "lodash/take";
import forOwn from "lodash/forOwn";
import compact from "lodash/compact";
import { FacetedClassification, FacetedQuery } from "immfacet";
import {
	BRANDS,
	LISTING_ITEM_TYPES,
	OFFER_PRICE_TYPES,
	PRODUCTS_FILTERS_KEY,
	RESOLUTION,
} from "app/constants";
import { addOptionsToCloundinaryURL } from "app/utils/image/cloudinaryUtils";
import { fromJS } from "immutable";
import pick from "lodash/pick";
import uniq from "lodash/uniq";
import includes from "lodash/includes";
import sortBy from "lodash/sortBy";
import orderBy from "lodash/orderBy";
import flatten from "lodash/flatten";
import get from "lodash/get";
import { containsTrainTransportationTypes, getDayCount } from "app/utils/utils";
import isEmpty from "lodash/isEmpty";
import {
	FOURTH_MERCHANDISING_POSITION,
	SECOND_MERCHANDISING_POSITION,
	THIRD_MERCHANDISING_POSITION,
} from "src/@brand/appConstants";
import {
	getMerchandisingByPosition,
	getMerchPreviewStartDate,
	getVisibleMerchandisings,
} from "app/pages/Merchandising/merchandisingSelector";
import { getCurrentLandingPage } from "app/pages/LandingPage/Auth/LandingAuthSelector"; // TODO : remove after ABTest
import memoize from "lodash/memoize";
import { zonedTimeToUtc } from "date-fns-tz";

import { sortProducts } from "app/utils/utils.js";

const getProducts = state => state.products;
const getProductsFilters = state => state.productsFilters;
const getTopCountries = state =>
	state.productsFilterMetaData && state.productsFilterMetaData.topCountries;
const getResolution = state => state.resolution;
const getBrand = state => state.brand.code;
const getCurrentlyViewedProduct = state => state.ficheProduit;

export const getOngoingProducts = createSelector(
	[getProducts, getMerchPreviewStartDate],
	(products = [], previewStartDate) => {
		// previewStartDate est valorisé par la query param ?preview=YYYYMMDD
		const previewDateTimestamp = zonedTimeToUtc(previewStartDate, "YYYYMMDD");

		return products.filter(product => {
			const productEndAtTimestamp = new Date(product.endAt).getTime();
			const productStartAtTimestamp = new Date(product.startAt).getTime();
			if (
				previewDateTimestamp &&
				(previewDateTimestamp >= productEndAtTimestamp ||
					previewDateTimestamp < productStartAtTimestamp)
			) {
				return false;
			}

			return true;
		});
	}
);

export const getProductsForHome = createSelector(
	[getOngoingProducts],
	(products = []) => {
		return take(products, 3);
	}
);

export const getProductsForLandingPage = createSelector(
	[getOngoingProducts, getCurrentLandingPage],
	(products = [], landingPage) => {
		if (get(landingPage, "productsUri.length") > 0) {
			return products.filter(product => {
				return includes(landingPage.productsUri, product.uri);
			});
		}

		return take(products, 3);
	}
);

export const getProductsByUri = (productUris = []) => {
	return createSelector(
		[getOngoingProducts],
		(products = []) => {
			const selectedProducts = productUris.map(productUri => {
				return products.find(product => {
					return product.uri === productUri;
				});
			});

			return compact(selectedProducts);
		}
	);
};

const getProductsFacetQuery = createSelector(
	[getOngoingProducts, getProductsFilters],
	(products = [], productsFilters = {}) => {
		let facetCollection = new FacetedClassification(fromJS(products));

		facetCollection = facetCollection
			.addFacet(
				PRODUCTS_FILTERS_KEY.TOPIC,
				product => {
					if (!product.get("topics")) {
						return undefined;
					}
					return product.get("topics").toJS();
				},
				{
					multiValue: true,
				}
			)
			.addFieldFacet(PRODUCTS_FILTERS_KEY.MERCHANDISINGS, {
				multiValue: true,
			})
			.addFacet(PRODUCTS_FILTERS_KEY.DESTINATION, product => {
				if (!product.get("country")) {
					return undefined;
				}
				return product.get("country");
			})
			.addFacet(
				PRODUCTS_FILTERS_KEY.ZONE,
				product => {
					if (!product.get("zones")) {
						return undefined;
					}
					return product
						.get("zones")
						.toJS()
						.map(zone => {
							return zone;
						});
				},
				{
					multiValue: true,
				}
			)
			.addFacet(
				PRODUCTS_FILTERS_KEY.CATEGORY,
				product => {
					if (!product.get("merchandisingTopics")) {
						return undefined;
					}
					return product.get("merchandisingTopics").toJS();
				},
				{
					multiValue: true,
				}
			)
			.addFacet(
				PRODUCTS_FILTERS_KEY.BADGE,
				product => {
					if (!product.get("badges")) {
						return undefined;
					}
					return product
						.get("badges")
						.toJS()
						.map(badge => {
							return badge.label;
						});
				},
				{
					multiValue: true,
				}
			)
			.addFacet(
				PRODUCTS_FILTERS_KEY.PERIOD,
				product => {
					if (!product.get("periods")) {
						return undefined;
					}
					return product
						.get("periods")
						.toJS()
						.map(period => {
							return period.label;
						});
				},
				{
					multiValue: true,
				}
			)
			.addFacet(
				PRODUCTS_FILTERS_KEY.MONTH,
				product => {
					if (!product.get("months")) {
						return undefined;
					}
					return product
						.get("months")
						.toJS()
						.map(month => {
							return String(month.startAt);
						});
				},
				{
					multiValue: true,
				}
			)
			.addFacet(PRODUCTS_FILTERS_KEY.START_AT, product => {
				if (!product.get("startAt")) {
					return undefined;
				}
				return String(
					getDayCount(product.get("startAt"), Date.now()) <= 2 &&
						getDayCount(product.get("startAt"), Date.now()) >= 0
				);
			})
			.addFacet(PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE, product => {
				if (!product.get("fromPriceType")) {
					return undefined;
				}

				return String(
					product.get("fromPriceType").toJS().type ===
						OFFER_PRICE_TYPES.FROM_PRICE_TYPE_SAVE_UP_TO
						? Number(product.get("fromPriceType").toJS().value) >= 50
						: undefined
				);
			})
			.addFacet(PRODUCTS_FILTERS_KEY.END_AT, product => {
				if (!product.get("endAt")) {
					return undefined;
				}

				return String(
					getDayCount(Date.now(), product.get("endAt")) <= 2 &&
						getDayCount(Date.now(), product.get("endAt")) >= 0
				);
			});

		let facetQuery = new FacetedQuery(facetCollection);

		forOwn(productsFilters, (value, key) => {
			// on doit gérer les valeurs en string et boolean (pour startAt et endAt)
			if ((value && value.length > 0) || (value && typeof value === "boolean")) {
				facetQuery = facetQuery.select({
					name: key,
					values:
						typeof value === "string" || typeof value === "boolean" ? [value] : value,
				});
			}
		});

		return facetQuery;
	}
);

export const getVisibleProducts = createSelector(
	[getProductsFacetQuery],
	(facetQuery = {}) => {
		return sortProducts({ products: facetQuery.selectedItems().toJS() });
	}
);

export const getVisiblesProductsRange1 = createSelector(
	[getVisibleProducts],
	(products = []) => {
		return products.slice(0, SECOND_MERCHANDISING_POSITION - 1);
	}
);

export const getVisiblesProductsRange2 = createSelector(
	[getVisibleProducts],
	(products = []) => {
		// Si les positions des merch 2 et 3 se suivent, alors on n'affiche pas de produit entre les 2 merch
		const end =
			THIRD_MERCHANDISING_POSITION - SECOND_MERCHANDISING_POSITION === 1
				? 0
				: THIRD_MERCHANDISING_POSITION - 1;
		return products.slice(SECOND_MERCHANDISING_POSITION - 1, end);
	}
);

export const getVisiblesProductsRange3 = createSelector(
	[getVisibleProducts],
	(products = []) => {
		const beginning =
			THIRD_MERCHANDISING_POSITION - SECOND_MERCHANDISING_POSITION === 1
				? SECOND_MERCHANDISING_POSITION - 1
				: THIRD_MERCHANDISING_POSITION - 1;
		const end =
			FOURTH_MERCHANDISING_POSITION - THIRD_MERCHANDISING_POSITION === 1
				? 0
				: FOURTH_MERCHANDISING_POSITION - 1 || products.length;
		return products.slice(beginning, end);
	}
);

export const getVisiblesProductsRange4 = createSelector(
	[getVisibleProducts],
	(products = []) => {
		const beginning =
			FOURTH_MERCHANDISING_POSITION - THIRD_MERCHANDISING_POSITION === 1
				? THIRD_MERCHANDISING_POSITION - 1
				: FOURTH_MERCHANDISING_POSITION - 1 || products.length;

		return products.slice(beginning, products.length);
	}
);

export const getVisibleFilters = createSelector(
	[getProductsFacetQuery],
	(facetQuery = {}) => {
		return facetQuery.selectedFacetValues().toJS() || [];
	}
);

export const getSelectedFacets = createSelector(
	[getProductsFacetQuery],
	(facetQuery = {}) => {
		return facetQuery.selectedFacets().toJS() || [];
	}
);

export const getTopicFilterValues = createSelector(
	[getOngoingProducts],
	(products = []) => {
		if (!products || products.length === 0) {
			return [];
		}

		const topics = products.map(product => {
			return product.topics || [];
		});

		return sortBy(uniq(flatten(topics)));
	}
);

export const hasTrainTransportation = createSelector(
	[getCurrentlyViewedProduct],
	currentProduct => {
		return containsTrainTransportationTypes(currentProduct.transportationTypes);
	}
);

export const getMerchandisingTopicsFilterValues = createSelector(
	[getOngoingProducts],
	(products = []) => {
		if (!products || products.length === 0) {
			return [];
		}

		const merchandisingTopics = products.map(product => {
			return product.merchandisingTopics || [];
		});

		return sortBy(uniq(flatten(merchandisingTopics)));
	}
);

export const getDestinationFilterValues = createSelector(
	[getOngoingProducts],
	(products = []) => {
		if (!products || products.length === 0) {
			return [];
		}

		const countries = products.map(product => {
			return product.country || [];
		});

		return sortBy(uniq(flatten(countries)));
	}
);

export const getTopDestinationFilterValues = createSelector(
	[getTopCountries],
	(topCountries = []) => {
		return topCountries;
	}
);

export const getZoneFilterValues = createSelector(
	[getOngoingProducts],
	(products = []) => {
		const zones = products.map(product => {
			if (!product.zones || product.zones.length === 0) {
				return [];
			}
			return product.zones || [];
		});
		return sortBy(uniq(flatten(zones)));
	}
);

export const getPeriodFilterValues = createSelector(
	[getOngoingProducts],
	(products = []) => {
		const periods = products.map(product => {
			if (!product.periods || product.periods.length === 0) {
				return [];
			}
			return product.periods || [];
		});

		const sortedPeriods = sortBy(flatten(periods), "startAt");

		const labels = sortedPeriods.map(period => {
			return pick(period, "label").label || [];
		});

		return uniq(labels);
	}
);

export const getMonthFilterValues = createSelector(
	[getOngoingProducts],
	(products = []) => {
		const months = products.map(product => {
			if (!product.months || product.months.length === 0) {
				return [];
			}
			return product.months || [];
		});

		const sortedMonths = sortBy(flatten(months), "startAt");

		const labels = sortedMonths.map(month => {
			return String(pick(month, "startAt").startAt) || [];
		});

		return uniq(labels);
	}
);

export const getBadgeFilterValues = createSelector(
	[getOngoingProducts],
	(products = []) => {
		const badges = products.map(product => {
			if (!product.badges || product.badges.length === 0) {
				return [];
			}
			return product.badges || [];
		});

		const firstBadges = badges.map(badgeList => {
			return badgeList[0];
		});

		const sortedBadges = sortBy(flatten(compact(firstBadges)), "label");

		const badgeLabels = sortedBadges.map(badge => {
			return pick(badge, "label").label || [];
		});

		return uniq(badgeLabels);
	}
);

export const isAnyListingFilterActive = createSelector(
	[getProductsFilters],
	productsFilters => {
		return !isEmpty(productsFilters);
	}
);

/**
 * Recupere la liste des urls des images à precharger en priorite lors du chargement de la page.
 * Les urls dépendent de :
 * - resolution
 * - brand (ProductOriginal ou ProductTranslucent)
 */
export const getPrefetchedListingPhotoUrls = createSelector(
	[getOngoingProducts, getResolution, getBrand],
	(products = [], resolution, brand) => {
		const isProductTransluscent =
			brand === BRANDS.EK ||
			brand === BRANDS.DD ||
			brand === BRANDS.MS ||
			brand === BRANDS.TO;

		const productPhotoUrls = take(products, 2)
			.filter(product => product.photos && product.photos[0])
			.map(product => {
				const firstProductPhoto = product.photos && product.photos[0];

				// même options que le composant src/app/pages/Listing/components/Product.jsx
				let cloudinaryOptions = ["w_768", "h_416", "c_fill"];

				if (resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE) {
					// même options que le composant src/app/pages/Listing/components/ProductFull.jsx
					cloudinaryOptions = isProductTransluscent
						? ["w_502", "h_290", "c_fill", "g_center"]
						: ["w_502", "h_240", "c_fill", "g_center"];
				}

				return addOptionsToCloundinaryURL(firstProductPhoto.url, cloudinaryOptions);
			});

		return productPhotoUrls;
	}
);

export const getPrefetchedMerchandisingPhotoUrls = createSelector(
	[getVisibleMerchandisings, getResolution, getBrand],
	(merchandisings = [], resolution, brandCode) => {
		const MERCH_COUNT = {
			AF: 3,
			EK: 3,
			TO: 1,
		};

		const MERCH_CLOUDINARY_OPTIONS = {
			AF: {
				[RESOLUTION.XLARGE]: ["w_1440", "h_550", "c_crop"],
				[RESOLUTION.LARGE]: ["w_1440", "h_550", "c_crop"],
				[RESOLUTION.MEDIUM]: ["w_768", "h_834", "c_fill", "g_center"],
				[RESOLUTION.SMALL]: ["w_768", "h_834", "c_fill", "g_center"],
			},
			EK: {
				[RESOLUTION.XLARGE]: ["w_500", "c_fill", "g_center"],
				[RESOLUTION.LARGE]: ["w_500", "c_fill", "g_center"],
				[RESOLUTION.MEDIUM]: ["w_768", "c_fill", "g_center"],
				[RESOLUTION.SMALL]: ["w_500", "c_fill", "g_center"],
			},
			TO: {
				[RESOLUTION.XLARGE]: ["w_1440"],
				[RESOLUTION.LARGE]: ["w_1440"],
				[RESOLUTION.MEDIUM]: ["w_768"],
				[RESOLUTION.SMALL]: ["w_768"],
			},
		};

		const merchCount = MERCH_COUNT[brandCode] || 0;

		const firstMerchs = take(
			orderBy(merchandisings, ["position", "headerIndex"], ["asc", "asc"]),
			merchCount
		);

		return firstMerchs.map(merch => {
			let url =
				resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE
					? get(merch, "photoLarge.url")
					: get(merch, "photoSmall.url");

			return addOptionsToCloundinaryURL(url, MERCH_CLOUDINARY_OPTIONS[brandCode][resolution]);
		});
	}
);

export const getPrefetchedPhotos = createSelector(
	[getPrefetchedListingPhotoUrls, getPrefetchedMerchandisingPhotoUrls],
	(prefetchedListingPhotoUrls = [], prefetchedMerchandisingPhotoUrls = []) => {
		if (!prefetchedListingPhotoUrls) {
			return prefetchedMerchandisingPhotoUrls;
		}

		return prefetchedListingPhotoUrls.concat(prefetchedMerchandisingPhotoUrls);
	}
);

export const getListingItems = memoize(idSpotlight =>
	createSelector(
		getVisiblesProductsRange1,
		getVisiblesProductsRange2,
		getVisiblesProductsRange3,
		getVisiblesProductsRange4,
		getMerchandisingByPosition(SECOND_MERCHANDISING_POSITION, idSpotlight),
		getMerchandisingByPosition(THIRD_MERCHANDISING_POSITION, idSpotlight),
		getMerchandisingByPosition(FOURTH_MERCHANDISING_POSITION, idSpotlight),
		(productsRange1, productsRange2, productsRange3, productsRange4, ...merchandisings) => {
			const formattedMerchandisings = merchandisings
				.map(merch =>
					get(merch, "merchList.length") > 1
						? {
								type: LISTING_ITEM_TYPES.MERCH_CAROUSSEL,
								position: merch.position,
								merchandising: merch.merchList,
						  }
						: {
								type: LISTING_ITEM_TYPES.MERCH_SIMPLE,
								position: merch.position,
								merchandising: merch.merchList[0],
						  }
				)
				.filter(merch => merch.merchandising);

			const items = [
				...productsRange1.map(product => {
					product.type = LISTING_ITEM_TYPES.PRODUCT;
					return product;
				}),
				formattedMerchandisings.find(
					merch => merch.position === SECOND_MERCHANDISING_POSITION
				),
				...productsRange2.map(product => {
					product.type = LISTING_ITEM_TYPES.PRODUCT;
					return product;
				}),
				formattedMerchandisings.find(
					merch => merch.position === THIRD_MERCHANDISING_POSITION
				),
				...productsRange3.map(product => {
					product.type = LISTING_ITEM_TYPES.PRODUCT;
					return product;
				}),
				formattedMerchandisings.find(
					merch => merch.position === FOURTH_MERCHANDISING_POSITION
				),
				...productsRange4.map(product => {
					product.type = LISTING_ITEM_TYPES.PRODUCT;
					return product;
				}),
			].filter(item => item);
			return items;
		}
	)
);
